import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiX, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import Toggle from 'react-toggle';
import 'react-toggle/style.css';
import { partnersAPI, extensionsAPI } from '../services/api';
import './AddPartner.css'; // Reuse the same CSS

const EditPartner = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  console.log('EditPartner component rendering for ID:', id);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    company: '',
    partnerType: 'partner',
    role: 'partner',
    balance: 0,
    isBalanceUnlimited: false,
    status: 'active'
  });
  
  // State for extension-specific settings
  const [extensionSettings, setExtensionSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPartner, setLoadingPartner] = useState(true);
  const [extensions, setExtensions] = useState([]);
  const [loadingExtensions, setLoadingExtensions] = useState(true);
  const [assignedExtensions, setAssignedExtensions] = useState([]);

  // Fetch partner data on component mount
  useEffect(() => {
    const fetchPartner = async () => {
      try {
        console.log('Fetching partner data...');
        setLoadingPartner(true);
        const response = await partnersAPI.getById(id);
        console.log('Partner response:', response);
        
        if (response.success && response.partner) {
          const partner = response.partner;
          setFormData({
            firstName: partner.firstName || '',
            lastName: partner.lastName || '',
            email: partner.email || '',
            phoneNumber: partner.phoneNumber || '',
            company: partner.company || '',
            partnerType: partner.partnerType || 'partner',
            role: partner.role || 'partner',
            balance: partner.balance || 0,
            isBalanceUnlimited: partner.isBalanceUnlimited || false,
            status: partner.status || 'active'
          });
          
          // Set assigned extensions
          if (partner.assignedExtensions && partner.assignedExtensions.length > 0) {
            console.log('Partner assigned extensions:', JSON.stringify(partner.assignedExtensions, null, 2));
            const assignedExts = partner.assignedExtensions.map(ext => {
              // Handle both direct extensionId and nested extension object
              console.log('Raw extension assignment object:', ext);
              // ext.extensionId is the full extension object, so we need to get its _id
              const extId = (ext.extensionId && ext.extensionId._id) || (ext.extension && ext.extension._id) || ext._id;
              console.log('Processing extension assignment:', ext, '->', extId);
              return extId;
            });
            console.log('Processed assigned extension IDs:', JSON.stringify(assignedExts, null, 2));
            setAssignedExtensions(assignedExts);
            
            // Set extension settings
            const settings = {};
            partner.assignedExtensions.forEach(ext => {
              const extId = (ext.extensionId && ext.extensionId._id) || (ext.extension && ext.extension._id) || ext._id;
              settings[extId] = {
                linkManager: ext.linkManager || false,
                extensionBalance: ext.extensionBalance || 0,
                isExtensionBalanceUnlimited: ext.isExtensionBalanceUnlimited || false
              };
            });
            setExtensionSettings(settings);
          }
        } else {
          toast.error('Failed to load partner data');
          navigate('/partners');
        }
      } catch (error) {
        console.error('Failed to fetch partner:', error);
        toast.error('Failed to load partner data');
        navigate('/partners');
      } finally {
        setLoadingPartner(false);
      }
    };

    if (id) {
      fetchPartner();
    }
  }, [id, navigate]);

  // Fetch extensions on component mount
  useEffect(() => {
    const fetchExtensions = async () => {
      try {
        console.log('Fetching extensions...');
        setLoadingExtensions(true);
        const response = await extensionsAPI.getAll();
        console.log('Extensions response:', response);
        
        // Handle different response formats
        let extensionsData = [];
        if (response && response.success && response.extensions) {
          extensionsData = response.extensions;
        } else if (response && response.data) {
          extensionsData = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          extensionsData = response;
        }
        
        console.log('Processed extensions data:', extensionsData);
        setExtensions(extensionsData);
        
        // Initialize extension settings for unassigned extensions
        const initialSettings = { ...extensionSettings };
        extensionsData.forEach(ext => {
          const extId = ext._id || ext.id;
          if (!initialSettings[extId]) {
            initialSettings[extId] = {
              linkManager: false,
              extensionBalance: 0,
              isExtensionBalanceUnlimited: false
            };
          }
        });
        setExtensionSettings(initialSettings);
      } catch (error) {
        console.error('Failed to fetch extensions:', error);
        toast.error('Failed to load extensions');
        setExtensions([]);
      } finally {
        setLoadingExtensions(false);
      }
    };

    fetchExtensions();
  }, []);

  // Monitor extension balance values and update master balance
  useEffect(() => {
    if (Object.keys(extensionSettings).length === 0) return;
    
    const hasAssignedExtensions = assignedExtensions.length > 0;
    
    if (hasAssignedExtensions && !formData.isBalanceUnlimited) {
      // Get all non-unlimited extension balances
      const assignedExtensionBalances = assignedExtensions
        .map(extId => extensionSettings[extId])
        .filter(settings => settings && !settings.isExtensionBalanceUnlimited)
        .map(settings => settings.extensionBalance || 0);
      
      if (assignedExtensionBalances.length > 0) {
        // Check if all assigned extensions have the same balance
        const allSameBalance = assignedExtensionBalances.every(
          balance => balance === assignedExtensionBalances[0]
        );
        
        if (allSameBalance && assignedExtensionBalances[0] !== parseInt(formData.balance)) {
          setFormData(prev => ({
            ...prev,
            balance: assignedExtensionBalances[0]
          }));
        }
      }
    }
  }, [extensionSettings, assignedExtensions, formData.isBalanceUnlimited]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'isBalanceUnlimited') {
      // Master unlimited balance toggle - affects all extensions
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      
      // Update all extension balance settings
      setExtensionSettings(prev => {
        const updatedSettings = {};
        Object.keys(prev).forEach(extId => {
          updatedSettings[extId] = {
            ...prev[extId],
            isExtensionBalanceUnlimited: checked
          };
        });
        return updatedSettings;
      });
    } else if (name === 'balance') {
      // Master balance input - affects all assigned extensions
      const balanceValue = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: balanceValue
      }));
      
      // Update all assigned extension balances (only if not unlimited)
      if (!formData.isBalanceUnlimited) {
        setExtensionSettings(prev => {
          const updatedSettings = { ...prev };
          assignedExtensions.forEach(extId => {
            if (updatedSettings[extId]) {
              updatedSettings[extId] = {
                ...updatedSettings[extId],
                extensionBalance: balanceValue,
                isExtensionBalanceUnlimited: false
              };
            }
          });
          return updatedSettings;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleExtensionToggle = (extensionId) => {
    setAssignedExtensions(prev => 
      prev.includes(extensionId)
        ? prev.filter(id => id !== extensionId)
        : [...prev, extensionId]
    );
  };

  const handleLinkManagerToggle = (extensionId) => {
    setExtensionSettings(prev => ({
      ...prev,
      [extensionId]: {
        ...prev[extensionId],
        linkManager: !prev[extensionId]?.linkManager
      }
    }));
  };

  const handleExtensionBalanceChange = (extensionId, value) => {
    setExtensionSettings(prev => ({
      ...prev,
      [extensionId]: {
        ...prev[extensionId],
        extensionBalance: parseInt(value) || 0
      }
    }));
  };

  const handleExtensionBalanceUnlimitedToggle = (extensionId) => {
    setExtensionSettings(prev => {
      const newSettings = {
        ...prev,
        [extensionId]: {
          ...prev[extensionId],
          isExtensionBalanceUnlimited: !prev[extensionId]?.isExtensionBalanceUnlimited
        }
      };
      
      // Check if any extension is not unlimited
      const hasNonUnlimited = Object.values(newSettings).some(
        setting => !setting.isExtensionBalanceUnlimited
      );
      
      // Update master unlimited balance based on extension settings
      if (hasNonUnlimited && formData.isBalanceUnlimited) {
        setFormData(prev => ({
          ...prev,
          isBalanceUnlimited: false
        }));
      }
      
      return newSettings;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare assigned extensions with their settings
      const assignedExtensionsWithSettings = assignedExtensions.map(extId => ({
        extensionId: extId,
        linkManager: extensionSettings[extId]?.linkManager || false,
        extensionBalance: extensionSettings[extId]?.isExtensionBalanceUnlimited ? 0 : (extensionSettings[extId]?.extensionBalance || 0),
        isExtensionBalanceUnlimited: extensionSettings[extId]?.isExtensionBalanceUnlimited || false
      }));

      // Prepare partner data for API
      const partnerData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        company: formData.company,
        partnerType: formData.partnerType,
        role: formData.role,
        balance: formData.isBalanceUnlimited ? 0 : (parseInt(formData.balance) || 0),
        isBalanceUnlimited: formData.isBalanceUnlimited,
        status: formData.status,
        assignedExtensions: assignedExtensionsWithSettings
      };

      console.log('Sending partner update data to API:', partnerData);

      const response = await partnersAPI.update(id, partnerData);
      console.log('API response:', response);
      
      if (response.success) {
        toast.success('Partner updated successfully!');
        navigate('/partners');
      } else {
        toast.error(response.message || 'Failed to update partner');
      }
    } catch (error) {
      console.error('Failed to update partner:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      toast.error(error.message || 'Failed to update partner. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    navigate('/partners');
  };

  // Add loading state for the entire page
  if (loadingPartner || loadingExtensions) {
    return (
      <div className="add-partner-container">
        <div className="page-header">
          <div className="page-title">
            <h2>Edit Partner</h2>
            <p>Update partner information and extension assignments</p>
          </div>
          <button className="close-btn" onClick={closeModal}>
            <FiX />
          </button>
        </div>
        <div className="add-partner-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading partner data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-partner-container">
      <div className="page-header">
        <div className="page-title">
          <h2>Edit Partner</h2>
          <p>Update partner information and extension assignments</p>
        </div>
        <button className="close-btn" onClick={closeModal}>
          <FiX />
        </button>
      </div>

      <div className="add-partner-content">
        <form onSubmit={handleSubmit} className="partner_add_form">
          <div className="formSection">
            <div className="formRow">
              <div className="half">
                <label htmlFor="firstName">Partner's First Name</label>
                <input
                  type="text"
                  placeholder="Enter"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label htmlFor="lastName">Partner's Last Name</label>
                <input
                  type="text"
                  placeholder="Enter"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label htmlFor="email">Partner's Email</label>
                <input
                  type="email"
                  placeholder="Enter"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label htmlFor="phoneNumber">Partner's Mobile No.</label>
                <input
                  type="tel"
                  placeholder="Enter"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="half">
                <label>Company</label>
                <input
                  type="text"
                  placeholder="Enter company name"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              <div className="half">
                <label>Partner Type</label>
                <select
                  name="partnerType"
                  value={formData.partnerType}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="ceo">CEO (Extension Creation)</option>
                  <option value="whitelabeler">Whitelabeler (Rebrand Access)</option>
                  <option value="reseller">Reseller (Sales & Distribution)</option>
                </select>
                <div className={`partnerTypeInfo ${formData.partnerType}`}>
                  {formData.partnerType === 'partner' && "Can create and sell extensions (includes whitelabeler and reseller abilities)"}
                  {formData.partnerType === 'whitelabeler' && "Can download and sell assigned whitelabeled extensions"}
                  {formData.partnerType === 'reseller' && "Can sell assigned extensions and manage EndUsers"}
                </div>
              </div>
              <div className="half">
                <label>Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="partner">Partner (Standard Access)</option>
                  <option value="admin">Admin (Enhanced Access)</option>
                  <option value="superadmin">Superadmin (Full Access)</option>
                </select>
                <div className={`roleInfo ${formData.role}`}>
                  {formData.role === 'partner' && "Standard partner access with basic permissions"}
                  {formData.role === 'admin' && "Admin access with enhanced permissions and user management"}
                  {formData.role === 'superadmin' && "Full system access with all permissions (use carefully)"}
                </div>
              </div>
              <div className="half">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="half">
                <label>Balance</label>
                <input
                  type="number"
                  name="balance"
                  value={formData.balance}
                  onChange={handleInputChange}
                  className="form-control"
                  min="0"
                  step="0.01"
                  disabled={formData.isBalanceUnlimited}
                />
                <div className="balanceInfo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label>
                    <input
                      type="checkbox"
                      name="isBalanceUnlimited"
                      checked={formData.isBalanceUnlimited}
                      onChange={handleInputChange}
                    />
                    Unlimited Balance
                  </label>
                  <button 
                    type="submit" 
                    className="btn btn-green btn-medium"
                    disabled={loading}
                    style={{ margin: 0 }}
                  >
                    {loading ? 'Updating...' : 'Update Partner'}
                  </button>
                </div>
              </div>
            </div>

            <div className="placeExtDeatils">
              <div className="headerExtTop">
                <div className="name">
                  <h3>Manage Extension Assignments</h3>
                  <div className="tagSpan">
                    {assignedExtensions.length}/{Array.isArray(extensions) ? extensions.length : 0}
                  </div>
                </div>
                <div className="action">
                  <button 
                    type="button" 
                    className="dropAssignBtn"
                    onClick={() => {
                      if (!Array.isArray(extensions) || extensions.length === 0) return;
                      const allIds = extensions.map(ext => ext._id || ext.id);
                      setAssignedExtensions(prev => 
                        prev.length === allIds.length ? [] : allIds
                      );
                    }}
                  >
                    {Array.isArray(extensions) && assignedExtensions.length === extensions.length ? 'Unassign All' : 'Assign All'}
                  </button>
                  <input type="search" placeholder="Search" />
                </div>
              </div>

              <ul className="extListForPartner">
                {Array.isArray(extensions) && extensions.length > 0 ? (
                  extensions.map(extension => {
                    const extensionId = extension._id || extension.id;
                    // Check if extension is assigned based on partner's actual assigned extensions
                    const isAssigned = assignedExtensions.includes(extensionId);
                    console.log(`Extension ${extension.name} (${extensionId}): isAssigned = ${isAssigned}, assignedExtensions =`, assignedExtensions);
                    console.log(`  - extensionId type: ${typeof extensionId}, value: ${extensionId}`);
                    console.log(`  - assignedExtensions types:`, assignedExtensions.map(id => typeof id));
                    const settings = extensionSettings[extensionId] || {};
                    
                    return (
                      <li key={extensionId} className={isAssigned ? 'selected' : ''}>
                        <div className="state">
                          <button
                            type="button"
                            className={`addIt ${isAssigned ? 'selected' : ''}`}
                            onClick={() => handleExtensionToggle(extensionId)}
                          >
                            {isAssigned ? <FiTrash2 /> : <FiPlus />}
                          </button>
                        </div>
                        <div className="extDetails">
                          <div className="extName">
                            {extension.name}
                            {extension.isWhitelabeled && <span className="whiteTag">White Labeled</span>}
                          </div>
                          <div className="extPlans">
                            <p>Extension Type</p>
                            <div className="plan">
                              <span className="planName">{extension.category || 'Extension'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="linkManager">
                          <div className="balanceHead">
                            <span>Link Manager</span>
                          </div>
                          <div className="unlimited">
                            <Toggle
                              checked={settings.linkManager || false}
                              onChange={() => handleLinkManagerToggle(extensionId)}
                              disabled={!isAssigned}
                              icons={false}
                              className="ios-toggle"
                            />
                          </div>
                        </div>
                        <div className="balance">
                          <div className="balanceHead">
                            <span>Assign Balance</span>
                          </div>
                          <div className="score">
                            <input
                              type="number"
                              value={settings.isExtensionBalanceUnlimited ? 0 : (settings.extensionBalance || 0)}
                              onChange={(e) => handleExtensionBalanceChange(extensionId, e.target.value)}
                              disabled={!isAssigned || settings.isExtensionBalanceUnlimited}
                              className="form-control"
                              min="0"
                              step="0.01"
                              style={{ width: '100%', marginBottom: '5px' }}
                            />
                          </div>
                          <div className="unlimited">
                            <div className="text">Unlimited</div>
                            <Toggle
                              checked={settings.isExtensionBalanceUnlimited || false}
                              onChange={() => handleExtensionBalanceUnlimitedToggle(extensionId)}
                              disabled={!isAssigned}
                              icons={false}
                              className="ios-toggle"
                            />
                          </div>
                          {isAssigned && formData.isBalanceUnlimited && (
                            <div className="balance-sync-indicator">
                              <span>✓</span>
                              <span>Synced with master</span>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="no-extensions">
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      {loadingExtensions ? 'Loading extensions...' : 'No extensions available'}
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>


        </form>
      </div>
    </div>
  );
};

export default EditPartner; 